import { fileUrl } from "@/lib/api";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Pencil, Trash2, Clock, CalendarOff, BarChart3 } from "lucide-react";
import CarAlertsPills from "@/components/vendor/CarAlertsPills";
import CarTcoChip from "@/components/car/CarTcoChip";
import VehicleStatusSwitcher from "@/components/ops/VehicleStatusSwitcher";
import Money from "@/components/Money";

const FALLBACK = "https://images.unsplash.com/photo-1576933765009-9f57f43910aa?auto=format&fit=crop&w=600&q=70";

const formatDay = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

export default function VendorCarsList({ cars, onEdit, onDelete, onTogglePaused }) {
  if (cars.length === 0) {
    return <div className="rounded-2xl border border-dashed border-border p-12 text-center text-neutral-500">No cars yet.</div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {cars.map((c) => (
        <div
          key={c.id}
          className={`rounded-2xl border overflow-hidden transition ${c.is_active === false ? "border-border opacity-70" : "border-border"}`}
          data-testid={`vendor-car-card-${c.id}`}
        >
          <div className="aspect-[4/3] bg-neutral-100 relative">
            <img
              src={c.images?.[0] ? (c.images[0].startsWith("http") ? c.images[0] : fileUrl(c.images[0])) : FALLBACK}
              alt=""
              className="w-full h-full object-cover"
            />
            {c.is_active === false && (
              <div
                className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-neutral-900/85 text-white text-xs font-medium"
                data-testid={`paused-badge-${c.id}`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span> Paused
              </div>
            )}
          </div>
          <div className="p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium truncate">{c.make} {c.model}</div>
                <div className="text-sm text-neutral-500 truncate">{c.location} · <Money amount={c.price_per_day} />/day</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button asChild variant="ghost" size="icon" data-testid={`car-finance-${c.id}`} aria-label="Financial report">
                  <Link to={`/dashboard/vendor?tab=reports&car=${c.id}`} title="View full financial report"><BarChart3 className="h-4 w-4 text-sky-600" /></Link>
                </Button>
                <Button variant="ghost" size="icon" onClick={() => onEdit(c)} data-testid={`edit-car-${c.id}`} aria-label="Edit"><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => onDelete(c.id)} data-testid={`delete-car-${c.id}`} aria-label="Delete"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
            {/* Schedule + unavailability hints */}
            {(c.scheduled_pause_at || (c.unavailable_ranges || []).length > 0) && (
              <div className="flex flex-wrap gap-1.5">
                {c.scheduled_pause_at && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200"
                    data-testid={`scheduled-pause-chip-${c.id}`}
                  >
                    <Clock className="h-2.5 w-2.5" />
                    Pauses {formatDay(c.scheduled_pause_at)}
                  </span>
                )}
                {(c.unavailable_ranges || []).length > 0 && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-700 border border-border"
                    data-testid={`unavail-count-chip-${c.id}`}
                    title={c.unavailable_ranges.map((r) => `${formatDay(r.start)} → ${formatDay(r.end)}${r.reason ? ` (${r.reason})` : ""}`).join("\n")}
                  >
                    <CalendarOff className="h-2.5 w-2.5" />
                    {c.unavailable_ranges.length} unavailable window{c.unavailable_ranges.length === 1 ? "" : "s"}
                  </span>
                )}
              </div>
            )}
            <CarAlertsPills car={c} />
            {/* Iter 97 — one-tap operational status switcher. */}
            <VehicleStatusSwitcher car={c} />
            {/* Iter 67bf — Total Cost of Ownership chip (vendor-only via API gate). */}
            <CarTcoChip carId={c.id} compact />
            <div className="flex items-center justify-between pt-3 border-t border-border">
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">{c.is_active === false ? "Paused" : "Active"}</div>
                <div className="text-xs text-neutral-500 mt-0.5">{c.is_active === false ? "Hidden from search" : "Visible to customers"}</div>
              </div>
              <Switch
                checked={c.is_active !== false}
                onCheckedChange={(v) => onTogglePaused(c, v)}
                data-testid={`toggle-active-${c.id}`}
                aria-label="Toggle active"
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
